import { Job, SupabaseJob } from '@applicator/shared'

import { Sentry, logger, startSpan } from '../../lib/sentry'
import { supabase } from '../../lib/supabase'
import { showError as showToastError } from '../../lib/toast'

import { UserRules } from './types'
import { mapSupabaseToJob } from './utils'

interface LoadJobsParams {
  searchQueryId: string | null | undefined
  userRules: UserRules
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setDisplayedJobsCount: React.Dispatch<React.SetStateAction<number>>
  setAllJobsLoaded: React.Dispatch<React.SetStateAction<boolean>>
}

export const createLoadJobs = ({
  searchQueryId,
  userRules,
  setJobs,
  setSupabaseJobs,
  setLoading,
  setError,
  setDisplayedJobsCount,
  setAllJobsLoaded,
}: LoadJobsParams) => {
  const loadJobs = async () => {
    setLoading(true)
    setError(null)

      // Set timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        logger.warn('Jobs loading timeout after 30 seconds')
        const errorMsg = 'Loading timeout. Please refresh the page.'
        setError(errorMsg)
        showToastError(errorMsg)
        setLoading(false)
      }, 30000)

    try {
      // STEP 1: Load minimal fields for fast initial render
      const minimalData = await startSpan(
        {
          op: 'db.query',
          name: searchQueryId ? `GET /jobs?searchQueryId=${searchQueryId} (minimal)` : 'GET /jobs (minimal)',
        },
        async (span: { setAttribute?: (key: string, value: string | number | boolean) => void } | null) => {
          let query = supabase
            .from('jobs')
            // Only essential fields for table display - no description, no full relevance data
            .select('id,job_no,title,company,location,salary_max,salary_currency,status,saved_at,search_query_id,is_relevant,relevance_score,rejected_from_relevant,rejected_reason,rejected_at,date_applied_mode,date_applied_manual,deadline_mode,deadline_manual,follow_up_mode,follow_up_manual')
            .order('saved_at', { ascending: false })

          // Filter by search_query_id if provided
          if (searchQueryId) {
            // Validate UUID format before querying
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
            if (!uuidRegex.test(searchQueryId)) {
              logger.error('Invalid searchQueryId format', { searchQueryId } as Record<string, unknown>)
              setError('Invalid search query ID format')
              setLoading(false)
              return null
            }
            // Show only jobs from this search query
            query = query.eq('search_query_id', searchQueryId)
            if (span) {
              span.setAttribute('filter', 'search_query_id')
              span.setAttribute('search_query_id', searchQueryId)
            }
          } else {
            // Show only jobs without search_query_id (general jobs, not from scraping)
            query = query.is('search_query_id', null)
            if (span) {
              span.setAttribute('filter', 'general_jobs')
            }
          }

          const { data, error: fetchError } = await query

          if (fetchError) {
            logger.error('Supabase query error', { error: fetchError.message, code: fetchError.code } as Record<string, unknown>)
            if (span) {
              span.setAttribute('error', true)
              span.setAttribute('error_message', fetchError.message)
            }
            const errorMsg = fetchError.message || 'Failed to load jobs'
            setError(errorMsg)
            showToastError(errorMsg)
            setLoading(false)
            return null
          }

          if (span) {
            span.setAttribute('jobs_count', data?.length || 0)
          }
          return data
        }
      )

      if (minimalData) {
        // Map minimal data to jobs with default values for missing fields
        const mappedJobs = minimalData.map((job: SupabaseJob) => mapSupabaseToJob({
          ...job,
          deadline_at: job.deadline_at || null,
          applied_at: job.applied_at || null,
          follow_up_at: job.follow_up_at || null,
          excitement: job.excitement || 0,
          relevance_score: job.relevance_score || null,
          relevance_verdict: job.relevance_verdict || null,
          relevance_reasoning: job.relevance_reasoning || null,
          date_applied_mode: job.date_applied_mode || 'auto',
          date_applied_manual: job.date_applied_manual || null,
          deadline_mode: job.deadline_mode || 'auto',
          deadline_manual: job.deadline_manual || null,
          follow_up_mode: job.follow_up_mode || 'auto',
          follow_up_manual: job.follow_up_manual || null,
        }, userRules))
        
        // Set jobs immediately for fast render
        setJobs(mappedJobs)
        setSupabaseJobs(minimalData as SupabaseJob[])
        setDisplayedJobsCount(20) // Reset to first 20
        setAllJobsLoaded(mappedJobs.length <= 20)
        
        // Hide loading immediately after minimal data is loaded
        setLoading(false)

        // STEP 2: Load additional fields in background (non-blocking)
        // Use requestIdleCallback if available, otherwise setTimeout
        const loadAdditionalFields = async () => {
          try {
            const jobIds = minimalData.map((j: SupabaseJob) => j.id)
            if (jobIds.length === 0) return

            const { data: additionalData, error: fetchError } = await supabase
              .from('jobs')
              .select('id,deadline_at,applied_at,follow_up_at,excitement,relevance_score,relevance_verdict,relevance_reasoning,rejected_from_relevant,rejected_reason,rejected_at,date_applied_mode,date_applied_manual,deadline_mode,deadline_manual,follow_up_mode,follow_up_manual')
              .in('id', jobIds)

            if (!fetchError && additionalData) {
              // Update jobs with additional data
              setSupabaseJobs((prev) => {
                return prev.map((job: SupabaseJob) => {
                  const additional = additionalData.find((a: SupabaseJob) => a.id === job.id)
                  if (additional) {
                    return { ...job, ...additional }
                  }
                  return job
                })
              })

              // Update mapped jobs with additional data
              setJobs((prevJobs) => {
                return prevJobs.map((job) => {
                  const additional = additionalData.find((a: SupabaseJob) => a.id === job.id)
                  if (additional) {
                    // Find the original supabase job and merge with additional data
                    const originalSupabaseJob = minimalData.find((j: SupabaseJob) => j.id === job.id)
                    if (originalSupabaseJob) {
                      const updatedSupabaseJob: SupabaseJob = { ...originalSupabaseJob, ...additional }
                      return mapSupabaseToJob(updatedSupabaseJob, userRules)
                    }
                  }
                  return job
                })
              })

              // Debug: log relevance data
              const jobsWithRelevance = additionalData.filter((j: SupabaseJob) => j.is_relevant !== null && j.is_relevant !== undefined)
              if (jobsWithRelevance.length > 0) {
                logger.info('Additional fields loaded in background', {
                  total: jobsWithRelevance.length,
                  relevant: jobsWithRelevance.filter((j: SupabaseJob) => j.is_relevant).length,
                  notRelevant: jobsWithRelevance.filter((j: SupabaseJob) => !j.is_relevant).length,
                } as Record<string, unknown>)
              }
            }
          } catch (err: unknown) {
            // Silently fail - this is background loading
            const errorMessage = err instanceof Error ? err.message : String(err)
            console.warn('[JOBS TABLE] Background field loading failed:', errorMessage)
          }
        }

        // Load additional fields after initial render
        if ('requestIdleCallback' in window) {
          requestIdleCallback(loadAdditionalFields, { timeout: 2000 })
        } else {
          setTimeout(loadAdditionalFields, 100)
        }
      }
    } catch (err: unknown) {
      clearTimeout(loadingTimeout)
      const errorMessage = err instanceof Error ? err.message : String(err)
      const errorStack = err instanceof Error ? err.stack : undefined
      logger.error('Unexpected error loading jobs', { error: errorMessage, stack: errorStack } as Record<string, unknown>)
      if (err instanceof Error) {
        Sentry.captureException(err)
      }
      const errorMsg = errorMessage || 'Failed to load jobs'
      setError(errorMsg)
      showToastError(errorMsg)
      setLoading(false)
    } finally {
      clearTimeout(loadingTimeout)
    }
  }

  return loadJobs
}
